import { AutoPositionStatus } from '@shure/shared/angular/data-access/system-api/core';
import {
	mapChannelGroupFromSysApi,
	mapFragmentFeaturesToModel,
	mapLobeChannelBeamWidthFromSysApi,
	mapMicPolarPatternFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { PositionPoint, PositionUnit } from '@shure/shared/angular/utils/utils-position';

import { CoverageAudioChannel } from '../../api/models';
import { DeviceCoverageAudioChannelFragment } from '../graphql/generated/device-coverage-audio-channel.generated';

import { mapToPositionUnitRange } from './coverage-device.mapper';

/**
 * Map raw (x,y) to PositionPoint.
 */
function mapToPositionPoint(xRaw: number, yRaw: number, zRaw?: number): PositionPoint {
	return {
		x: PositionUnit.fromRaw(xRaw),
		y: PositionUnit.fromRaw(yRaw),
		z: zRaw != null ? PositionUnit.fromRaw(zRaw) : undefined
	};
}

type CoverageAudioChannelDeviceContext = {
	id: string;
	model: DeviceModel;
	virtual: boolean;
	deviceHeight: number;
	usePostGateValues: boolean;
};

export function mapCoverageAudioChannelFromSysApi(
	fragment: DeviceCoverageAudioChannelFragment,
	deviceContext: CoverageAudioChannelDeviceContext
): CoverageAudioChannel {
	const {
		lobeConfiguration,
		lobePosition,
		lobeWidth,
		name,
		danteRouteTransmitter,
		identify,
		lobeTalkerHeight,
		mute,
		preGateMute,
		postGateMute,
		micPolarPattern,
		microphoneAngle,
		gain,
		postGateGain,
		autoPosition
	} = fragment.features;

	const {
		microphoneAngle: micAngleConstraints,
		gain: plainGainConstraints,
		postGateGain: postGateGainConstraints,
		lobeTalkerHeight: lobeTalkerHeightConstraints,
		lobePosition: lobePositionConstraints,
		name: nameConstraints
	} = fragment.description.constraints;

	const { id, model, virtual, deviceHeight, usePostGateValues } = deviceContext;

	const gainConstraints = usePostGateValues ? postGateGainConstraints : plainGainConstraints;

	// if the device doesn't support talkerHeight, beam length is lobePosition z.
	const beamLengthRaw =
		fragment.description.features.lobeTalkerHeight.supported && deviceHeight
			? lobeTalkerHeight?.fixed.height
				? deviceHeight - lobeTalkerHeight.fixed.height
				: 0
			: (lobePosition?.fixed.zPosition ?? 0);
	const beamLengthSource = fragment.description.features.lobeTalkerHeight.supported
		? 'lobeTalkerHeight'
		: 'lobePosition';
	const effectiveMicAngleRange = microphoneAngle?.fixedAngleRange ?? micAngleConstraints?.fixed.angle.range;

	return {
		id: fragment.id,
		index: fragment.description.index,
		enabled: lobeConfiguration?.enabled ?? false,
		group: mapChannelGroupFromSysApi(fragment.description.group),
		device: { id, model, virtual },
		constraints: {
			beam: lobePositionConstraints
				? {
						axisX: mapToPositionUnitRange(lobePositionConstraints.fixed.xPosition.range),
						axisY: mapToPositionUnitRange(lobePositionConstraints.fixed.yPosition.range)
					}
				: undefined,
			name: nameConstraints
				? {
						pattern: new RegExp(nameConstraints.name.pattern),
						length: { min: 1, max: 31 }
					}
				: undefined,
			micAngle: effectiveMicAngleRange
				? {
						min: effectiveMicAngleRange.min,
						max: effectiveMicAngleRange.max,
						step: effectiveMicAngleRange.step
					}
				: undefined,
			gain: gainConstraints
				? {
						min: gainConstraints.gain.range.min,
						max: gainConstraints.gain.range.max,
						step: gainConstraints.gain.range.step
					}
				: undefined,
			talkerHeight: lobeTalkerHeightConstraints
				? {
						min: lobeTalkerHeightConstraints.fixed.height.range.min,
						max: lobeTalkerHeightConstraints.fixed.height.range.max,
						step: lobeTalkerHeightConstraints.fixed.height.range.step
					}
				: undefined
		},
		features: mapFragmentFeaturesToModel(fragment, {
			name: () => ({
				value: name?.name ?? ''
			}),
			danteChannelName: {
				source: 'danteRouteTransmitter',
				map: () => ({ value: danteRouteTransmitter?.danteChannelName ?? '' })
			},
			identify: () => ({ value: identify?.identifying ?? false }),
			beamWidth: {
				source: 'lobeWidth',
				map: () => ({ value: mapLobeChannelBeamWidthFromSysApi(lobeWidth?.width) })
			},
			beamLength: {
				source: beamLengthSource,
				map: () => ({ value: PositionUnit.fromRaw(beamLengthRaw) })
			},
			beam: {
				source: 'lobePosition',
				map: () => ({
					value: mapToPositionPoint(
						lobePosition?.fixed.xPosition ?? 0,
						lobePosition?.fixed.yPosition ?? 0,
						lobePosition?.fixed.zPosition
					)
				})
			},
			talkerHeight: {
				source: 'lobeTalkerHeight',
				map: () => ({
					value: PositionUnit.fromRaw(lobeTalkerHeight?.fixed.height ?? 0)
				})
			},
			...(!virtual && {
				autofocusBeam: {
					source: 'lobePosition',
					map: () => ({
						value: mapToPositionPoint(
							lobePosition?.autoFocus?.xPosition ?? 0,
							lobePosition?.autoFocus?.yPosition ?? 0
						)
					})
				},
				autofocusTalkerHeight: {
					source: 'lobeTalkerHeight',
					map: () => ({
						value: PositionUnit.fromRaw(lobeTalkerHeight?.autoFocus?.height ?? 0)
					})
				},
				autofocusMicAngle: {
					source: 'microphoneAngle',
					map: () => ({
						value: microphoneAngle?.autoFocus?.angle
					})
				}
			}),
			...(!usePostGateValues
				? {
						mute: () => ({ value: { muted: mute?.muted ?? false, isPostGate: false } }),
						gain: () => ({ value: { gain: gain?.gain ?? 0, isPostGate: false } })
					}
				: {
						// Hack casting to 'never'
						mute: {
							source: 'postGateMute',
							map: () => ({ value: { muted: postGateMute?.muted ?? false, isPostGate: true } })
						} as never,
						gain: {
							source: 'postGateGain',
							map: () => ({ value: { gain: postGateGain?.gain ?? 0, isPostGate: true } })
						} as never
					}),
			micPolarPattern: () => ({
				value: mapMicPolarPatternFromSysApi(micPolarPattern?.pattern)
			}),
			micAngle: {
				source: 'microphoneAngle',
				map: () => ({ value: microphoneAngle?.fixed.angle ?? 0 })
			},
			autoPositionSupported: {
				source: 'autoPosition',
				map: () => ({ value: true })
			},
			autoPosition: () => ({ value: autoPosition?.status ?? AutoPositionStatus.Inactive }),
			preGateMute: () => ({ value: preGateMute?.muted ?? false })
		})
	};
}
